// The ordered list of chart-furniture pieces (R548).
//
// The order of the list is the order the pieces pack in, and therefore the
// order they draw in inside one tier; the tier itself decides which of the
// two engine layers they land on. The list is grouped by what a group
// stands ON, from the sea bed up:
//
//   1. the SEA pieces, which are the chart drawn on the water itself and
//      which the film keeps under it;
//   2. the world-space SCREEN pieces, which stand over the fleets and name
//      what a hull, a hold or a structure is doing now;
//   3. the screen-space SCREEN pieces, which frame the sheet and do not
//      move with the camera;
//   4. the DOM pieces, which are cards of words in the `#furniture` host.
//
// Inside a group the order is the CARD order of the pick, except three
// places. In the first group the depth tints of card 09 stand second,
// after the water-lining, because they are the colour of the water and
// a figure must draw over them (R2616), with the shoal stipple and the
// edge seam of cards 71 and 72 right after them (R2726), and the fairways
// of card 70 stand right before the shipping lanes of card 16, so the
// road of a lane draws over the glow of a fairway (R2723, R3072). In the third group the band
// frames the sheet, the graticule writes into the band, and the rose
// stands over both in the free corner.
//
// Card 40, the legend, is NOT here. It is a group inside the KEYS panel and
// draws nothing on the map, so it keeps its own gate in `ui/chart/legend.ts`
// and the overlay calls it. It is the ONE key of `CHART.pieces` that this
// list does not carry, and `registry.test.ts` pins that.

import type { AnyPiece } from './context';
// 1. The sea, in card order.
import { waterLines } from './pieces/water-lines';
import { depthTints } from './pieces/depth-tints';
import { edgeSeam, shoalStipple } from './pieces/depth-marks';
import { isobaths } from './pieces/isobaths';
import { soundings } from './pieces/soundings';
import { currentFlow } from './pieces/current-flow';
import { bottomNature } from './pieces/bottom-nature';
import { placeNames } from './pieces/place-names';
import { hazards } from './pieces/hazards';
import { wrecks } from './pieces/wrecks';
import { banks } from './pieces/banks';
import { harbourLimits } from './pieces/harbour-limits';
import { ribbons } from './pieces/ribbons';
import { marginWarning } from './pieces/margin-warning';
import { neatLine } from './pieces/neat-line';
import { imprint } from './pieces/imprint';
import { memorialReef } from './pieces/memorial-reef';
import { holdSite } from './pieces/hold-site';
import { poolMonument } from './pieces/pool-monument';
// 2. Over the fleets, in card order.
import { shipTracks } from './pieces/ship-tracks';
import { TidalDiamondPiece } from './pieces/tidal-diamond';
import { lightChars } from './pieces/light-chars';
import { coastalOutline } from './pieces/coastal-profile';
import { speedLabel } from './pieces/speed-label';
import { heraldry } from './pieces/heraldry';
import { cables } from './pieces/cables';
import { RestrictedPiece } from './pieces/restricted';
import { LimitLinePiece } from './pieces/limit-line';
import { bearingLines } from './pieces/bearing-lines';
import { RangeRingsPiece } from './pieces/range-rings';
import { torpedoRun } from './pieces/torpedo-run';
import { battleMarks } from './pieces/battle-marks';
import { epicentre } from './pieces/epicentre';
import { clanBurgee } from './pieces/clan-burgee';
import { spoilGround } from './pieces/spoil-ground';
import { works } from './pieces/works';
import { sonarSweep } from './pieces/sonar-sweep';
import { mapMarkers, mapMarkersLive } from './pieces/map-markers';
import { inboxPins } from './pieces/inbox-pins';
import { pointsOfInterest, pointsOfInterestGate, pointsOfInterestLive } from './pieces/points-of-interest';
import { cardinalMarks } from './pieces/cardinal-marks';
import { shippingLanes } from './pieces/shipping-lanes';
import { fairways } from './pieces/fairways';
import { hereBeMonsters } from './pieces/here-be-monsters';
import { designations } from './pieces/designations';
import { listeningWatch } from './pieces/listening-watch';
import { picketLine } from './pieces/picket-line';
// 3. The margins of the sheet.
import { borderBand } from './pieces/border-band';
import { graticule } from './pieces/graticule';
import { compassRose } from './pieces/compass-rose';
// 4. The cards of words.
import { cartouche } from '../../ui/chart/cartouche';
import { factionKey } from '../../ui/chart/faction-key';

/**
 * Every piece the client draws, in draw order. `ChartFurniture` takes this
 * list.
 *
 * Card 66 stands twice: the static and the live half of the map markers
 * are two objects of ONE id, so the gate, the zoom band and the two
 * readings are one answer for both (§17.3.66). It is the only id that
 * appears twice in this list. Card 18 is the outline of the hovered ridge
 * alone; its elevation is the body of the ridge peek (§17.3.18).
 */
export const CHART_PIECES: readonly AnyPiece[] = [
  // The sea itself: the water-lining, the ground, and the names of the
  // ground. The film KEEPS every piece of this group, because it is the sea
  // the fight happened in.
  waterLines,
  // The depth tints of v2 are the colour of the water itself, so they
  // stand before every other mark of the sea and out of card order: the
  // quads of one pack draw in the order they are written, and a figure
  // must stand over the tint (card 09, R2616).
  depthTints,
  // The shoal stipple (card 71) and the edge seam (card 72) of the depth
  // pass write no quad either: the depth layer draws them on the tints, so
  // they stand right after card 09, out of card order (R2726).
  shoalStipple,
  edgeSeam,
  // The soundings (card 02) and the current field (card 07) of v2
  // (R2616), in the look of the World field `currents` (R2716). The field
  // follows the tide of the season running now, so the film hides it.
  soundings,
  currentFlow,
  // The wrecks lie on the sea bed, in card order after the water-lining.
  // They belong to the season running now, so the film hides them (card
  // 08, R801).
  wrecks,
  isobaths,
  bottomNature,
  // The fairways of the map and their buoys (card 70, R2723). They stand
  // under the shipping lanes, because a lane runs through a fairway and the
  // road must stand over the glow; the film hides them.
  fairways,
  // The shipping lanes of v2 phase 6 (card 16, R2646), in the faction
  // ink. The lanes are of the season running now, so the film hides them.
  shippingLanes,
  placeNames,
  // The printer's imprint in the margin below the bottom-left corner
  // (card 24, R1852). It names the session, so the film hides it.
  imprint,
  hazards,
  banks,
  ribbons,
  // The gut marks of v2 phase 4 at each gut (card 50, R2637, R2723), on
  // the rock in card order. The NPC side plays in the world running now,
  // so the film hides them.
  cardinalMarks,
  // The margin of the sheet, in the void past the world edge (R802).
  marginWarning,
  neatLine,
  // The monuments of the winners of the seasons before this one (R964).
  // They are the sea tier and the film keeps them, because a monument was
  // laid before this season opened. They stand LAST in the group because
  // the group is ordered by card, and cards 63, 64 and 65 are the newest.
  memorialReef,
  holdSite,
  poolMonument,
  // The points of interest the survey of the faction holds (card 69,
  // R2626). They are the newest card of the group, and the film hides
  // them, because the survey is faction knowledge of today. The piece
  // stands twice, as the map markers do: the static half and the live half
  // of the set the reader picked (R2714).
  pointsOfInterest,
  pointsOfInterestLive,
  // Over the fleets: what one hull, one hold or one structure is doing in
  // the season running now. The film hides all of it.
  shipTracks,
  new TidalDiamondPiece(),
  lightChars,
  coastalOutline,
  speedLabel,
  heraldry,
  // The harbour limits of v2 round the hovered or selected hold (card 28,
  // R2585, R2727), in the dashed hex line of card 30, in card order. They
  // belong to the season running now, so the film hides them.
  harbourLimits,
  // The area of the peeked current gate (card 69, R2955), in the line of
  // card 28: the third object of card 69.
  pointsOfInterestGate,
  cables,
  new RestrictedPiece(),
  new LimitLinePiece(),
  // The sonar plot of v2 phase 3 (R2626): the designation of each contact
  // (card 41), before the lines of bearing in card order.
  designations,
  bearingLines,
  // The pieces of v2 phase 6 (R2657), in card order: the listening watch
  // of a picket buoy (card 47) and the picket line between two own fixed
  // listeners (49).
  new RangeRingsPiece(),
  listeningWatch,
  torpedoRun,
  picketLine,
  battleMarks,
  // Here be monsters of v2 phase 4 (card 53, R2637): the words under a
  // held monster and the mark of its last sighting, in card order.
  hereBeMonsters,
  epicentre,
  clanBurgee,
  spoilGround,
  works,
  sonarSweep,
  // The map markers of the squad and the faction (card 66, R1291). They
  // stand last in the group, over every other mark of the season, because
  // a marker is a message a commander placed on purpose. The piece stands
  // twice: the static half and the live half share one id and one gate
  // table (§17.3.66).
  mapMarkers,
  mapMarkersLive,
  // The inbox pins of NEEDS YOU (card 68, R1846). They stand over the map
  // markers, because a pin answers the INBOX the reader holds open.
  inboxPins,
  // The margins: the band frames the sheet, the graticule writes into it
  // and the rose stands in the free corner over both.
  borderBand,
  graticule,
  compassRose,
  // The cards of words, in the `#furniture` host.
  cartouche,
  // The key of the faction second channel (card 67, R1568). It stands in
  // the bottom-right stack, directly above the cartouche.
  factionKey,
];
